import React from 'react';
import { Link } from 'react-router-dom';
const BusinessManagement = () => {
  const programDetails = {
    title: 'Business & Management',
    degree: 'Bachelor of Business Administration (BBA)',
    duration: '3-4 Years',
    credits: '120-140 ECTS',
    intake: 'January, May, September',
    
    overview: 'Our Business & Management program provides students with a comprehensive understanding of modern business practices and management principles. The program combines theoretical knowledge with practical applications, preparing graduates for leadership roles in various business sectors.',
    
    highlights: [
      'Accredited by international business education bodies',
      'Industry-experienced faculty',
      'Internship opportunities with leading companies',
      'International exchange programs available',
      'Focus on entrepreneurship and innovation'
    ],
    
    specializations: [
      'Marketing Management',
      'Financial Management',
      'Human Resource Management',
      'International Business',
      'Entrepreneurship',
      'Digital Business'
    ],
    
    careerPaths: [
      'Business Consultant',
      'Marketing Manager',
      'Financial Analyst',
      'HR Manager',
      'Entrepreneur',
      'Operations Manager',
      'Business Development Manager'
    ],
    
    entryRequirements: [
      'High School Diploma or equivalent',
      'Foundation Program completion or equivalent',
      'English language proficiency (IELTS 6.0 or equivalent)',
      'Personal statement',
      'Interview (if required)'
    ]
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 py-20">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">{programDetails.title}</h1>
          <p className="text-2xl text-blue-100">{programDetails.degree}</p>
          <div className="flex flex-wrap gap-6 text-blue-100 mt-6">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Duration: {programDetails.duration}
            </div>
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {programDetails.credits} ECTS
            </div>
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Intake: {programDetails.intake}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Program Overview */}
            <section className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Program Overview</h2>
              <p className="text-gray-700 mb-6">{programDetails.overview}</p>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Program Highlights</h3>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {programDetails.highlights.map((highlight, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span className="text-gray-700">{highlight}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Specializations */}
            <section className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Specializations</h2>
              <p className="text-gray-700 mb-6">
                In the final year, students can choose to specialize in one of the following areas:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {programDetails.specializations.map((specialization, index) => (
                  <div key={index} className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-blue-800">{specialization}</h3>
                  </div>
                ))}
              </div>
            </section>

            {/* Career Paths */}
            <section className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Career Paths</h2>
              <p className="text-gray-700 mb-6">
                Graduates of our Business & Management program have gone on to successful careers in various sectors, including:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {programDetails.careerPaths.map((path, index) => (
                  <div key={index} className="flex items-center bg-gray-50 p-3 rounded-lg">
                    <span className="text-blue-600 mr-3">•</span>
                    <span className="text-gray-700">{path}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Entry Requirements */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Entry Requirements</h2>
              <ul className="space-y-3">
                {programDetails.entryRequirements.map((requirement, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span className="text-gray-700">{requirement}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/admissions/apply-now"
                className="mt-6 inline-block bg-blue-900 text-white px-6 py-2 rounded-md hover:bg-blue-800 transition-colors w-full text-center"
              >
                Apply Now
              </Link>
            </div>

            {/* Quick Links */}
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Related Programs</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/academics/foundation" className="text-blue-700 hover:underline">
                    Foundation Programme
                  </Link>
                </li>
                <li>
                  <Link to="/academics/computing-it" className="text-blue-700 hover:underline">
                    Computing & IT
                  </Link>
                </li>
                <li>
                  <Link to="/academics/tourism-hospitality" className="text-blue-700 hover:underline">
                    Tourism & Hospitality
                  </Link>
                </li>
                <li className="pt-2 mt-4 border-t border-blue-100">
                  <Link to="/admissions/request-info" className="text-blue-700 font-medium hover:underline">
                    Request More Information →
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-blue-900 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Start Your Business Career Today</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join our Business & Management program and gain the skills needed to succeed in the dynamic world of business.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/admissions/apply-now"
              className="bg-white text-blue-900 px-8 py-3 rounded-md font-medium hover:bg-blue-100 transition-colors"
            >
              Apply Now
            </Link>
            <Link
              to="/contact"
              className="border-2 border-white text-white px-8 py-3 rounded-md font-medium hover:bg-blue-800 transition-colors"
            >
              Contact Admissions
            </Link>
          </div>
        </div>
      </div>

      
    </div>
  );
};

export default BusinessManagement;
